import type { Cache } from '@urql/exchange-graphcache';
import { graphql } from '@/gql/graphql';
import rsvpTeamSummaries from './rsvpTeamSummaries';

const PersonRsvpOptimisticFragment = graphql(`
  fragment PersonRsvpOptimistic on person {
    id
    rsvp(args: { event_id: $eventId })
    rsvps(where: { event_id: { _eq: $eventId } }, limit: 1) {
      __typename
      event_id
      person_id
      status
      notes
    }
  }
`);

export type RsvpOptimisticCache = Pick<
  Cache,
  'writeFragment' | 'resolve' | 'inspectFields' | 'readFragment'
>;

const EventRsvpOptimisticFragment = graphql(`
  fragment EventRsvpOptimistic on event {
    id
    rsvp(args: { person_id: $personId })
  }
`);

export default function insert_rsvp_one(
  args: {
    object: ReturnType<typeof graphql.scalar<'rsvp_insert_input'>>;
    on_conflict: ReturnType<typeof graphql.scalar<'rsvp_on_conflict'>>;
  },
  cache: RsvpOptimisticCache,
) {
  if (!args.object.event_id || !args.object.person_id) {
    return null;
  }

  if (args.object.status === undefined) {
    return null;
  }

  const newStatus = args.object.status ?? null;
  const existingNotes = cache.resolve(
    {
      __typename: 'rsvp',
      event_id: args.object.event_id,
      person_id: args.object.person_id,
    },
    'notes',
  );
  let notes: string | null = null;
  if (args.object.notes !== undefined) {
    notes = args.object.notes;
  } else if (typeof existingNotes === 'string') {
    notes = existingNotes;
  }

  const result = {
    __typename: 'rsvp',
    event_id: args.object.event_id,
    notes,
    person_id: args.object.person_id,
    status: newStatus,
    event: {
      __typename: 'event',
      id: args.object.event_id,
      event_teams: rsvpTeamSummaries(
        cache,
        args.object.event_id,
        args.object.person_id,
        newStatus,
      ).map((summary) => ({
        __typename: 'event_team',
        event_id: summary.event_id,
        team_id: summary.team_id,
        rsvp_summary: summary,
      })),
    },
  };

  cache.writeFragment(
    EventRsvpOptimisticFragment,
    { id: args.object.event_id, rsvp: newStatus },
    { personId: args.object.person_id },
  );

  cache.writeFragment(
    PersonRsvpOptimisticFragment,
    {
      id: args.object.person_id,
      rsvp: newStatus,
      rsvps: [
        {
          __typename: 'rsvp',
          event_id: args.object.event_id,
          person_id: args.object.person_id,
          status: newStatus,
          notes: result.notes ?? null,
        },
      ],
    },
    { eventId: args.object.event_id },
  );

  return result;
}
